import { Types as TypesModule } from '@/app/sales_analysis/store/Index'
import { Types as TypesDateGranularity } from '@/app/sales_analysis/store/DateGranularity'
import filters, { Types as TypesFilters } from '@/app/shared/store/modules/Filters'
import breadCrumb, { Types as TypesBreadCrumb } from '@/app/sales_analysis/store/BreadCrumb'

import { getLongGrid, downloadLongGridToExcel } from '@/app/sales_analysis/Services'
import { addNamespace } from '@/app/shared/utils/NamespaceHelper'

const BREADCRUMB_MODULE_NAME = 'breadCrumb'
const NAMESPACE = 'salesAnalysis/longGrid/'
const NAMESPACE_BREADCRUMB = NAMESPACE + BREADCRUMB_MODULE_NAME + '/'

const _types = {
  getters: {
    PIVOT_DIMENSIONS: 'GETTER_PIVOT_DIMENSIONS',
    DIMENSION_TO_FILTER: 'GETTER_DIMENSION_TO_FILTER',
    DEFAULT_DIMENSION: 'GETTER_DEFAULT_DIMENSION',
    PIVOTED_FILTERS: 'GETTER_PIVOTED_FILTERS',
    PIVOTED_FILTER_DIMENSION_IDS: 'GETTER_PIVOTED_FILTER_DIMENSION_IDS',
    HAS_SELECTED_FILTERS: 'GETTER_HAS_SELECTED_FILTERS',
    BASE_DIMENSION: 'BASE_DIMENSION',
    LAST_BREADCRUMB_ITEM: 'LAST_BREADCRUMB_ITEM',
    CREATE_FILTER_OBJECT: 'CREATE_FILTER_OBJECT'
  },
  actions: {
    INIT: 'INIT',
    INIT_BREADCRUMB: 'INIT_BREADCRUMB',
    INIT_FILTERS: 'INIT_FILTERS',
    INIT_DIMENSION: 'INIT_DIMENSION',
    FETCH_LONG_GRID: 'FETCH_LONG_GRID',
    PUSH_BREADCRUMB_ITEM: 'PUSH_BREADCRUMB_ITEM',
    POP_UNTIL_BREADCRUMB: 'POP_UNTIL_BREADCRUMB',
    CHANGE_PIVOT: 'CHANGE_PIVOT',
    CHANGE_BASE_DIMENSION: 'CHANGE_BASE_DIMENSION',
    APPLY_FILTERS: 'APPLY_FILTERS',
    DOWNLOAD_EXCEL: 'DOWNLOAD_EXCEL',
    RESET: 'RESET',
    SET_INIT_DATA_DATE_GRANULARITY: 'SET_INIT_DATA_DATE_GRANULARITY'
  },
  mutations: {
    SET_DEFAULT_PARAMETERS: 'SET_DEFAULT_PARAMETERS',
    SET_GRID: 'SET_GRID'
  }
}
export const Types = addNamespace(NAMESPACE, _types)

const LongGrid = {
  namespaced: true,
  state: {
    grid: null,
    metrics: null
  },
  getters: {
    [_types.getters.DEFAULT_DIMENSION]: (state, getters, rootState, rootGetters) => {
      const defaultDimensions = rootGetters[TypesModule.getters.DEFAULT_DIMENSIONS]
      const dimensiondId = defaultDimensions.product_dimensions.length > 1
        ? defaultDimensions.product_dimensions[1]
        : defaultDimensions.store_dimensions[1]
      return rootGetters[TypesModule.getters.DIMENSIONS].find((dimension) => dimension.key === dimensiondId)
    },
    [_types.getters.PIVOT_DIMENSIONS]: (state, getters, rootState, rootGetters) => {
      const dimensionIdsInBreadCrumb = rootGetters[NAMESPACE_BREADCRUMB + TypesBreadCrumb.getters.DIMENSION_IDS_IN_BREADCRUMB]
      const dimensionsToPivot = (dimension) => !dimensionIdsInBreadCrumb.includes(dimension.id)
      return rootGetters[TypesModule.getters.DIMENSIONS].filter(dimensionsToPivot)
    },
    [_types.getters.PIVOTED_FILTERS]: (state) => {
      return state.breadCrumb.items.filter((breadcrumbItem) => breadcrumbItem.filter)
        .map((breadcrumbItem) => breadcrumbItem.filter)
    },
    [_types.getters.PIVOTED_FILTER_DIMENSION_IDS]: (state, getters) => {
      return getters[_types.getters.PIVOTED_FILTERS].map((pivotedFilter) => pivotedFilter.keyField)
    },
    [_types.getters.HAS_SELECTED_FILTERS]: (state, getters, rootState, rootGetters) => {
      return rootGetters[NAMESPACE + TypesFilters.getters.HAS_SELECTED_FILTERS]
    },
    [_types.getters.DIMENSION_TO_FILTER]: (state, getters, rootState, rootGetters) => {
      return rootGetters[NAMESPACE_BREADCRUMB + TypesBreadCrumb.getters.DIMENSION_TO_FILTER]
    },
    [_types.getters.BASE_DIMENSION]: (state, getters, rootState, rootGetters) => {
      return rootGetters[NAMESPACE_BREADCRUMB + TypesBreadCrumb.getters.FIRST_ITEM].dimension
    },
    [_types.getters.LAST_BREADCRUMB_ITEM]: (state, getters, rootState, rootGetters) => {
      return rootGetters[NAMESPACE_BREADCRUMB + TypesBreadCrumb.getters.LAST_ITEM]
    },
    [_types.getters.CREATE_FILTER_OBJECT]: (state) => (filter) => {
      const filterAttribute = `${filter.keyFieldType}_filters`
      return { [filterAttribute]: { [filter.keyField]: filter.value } }
    }
  },
  actions: {
    [_types.actions.INIT] ({ commit, dispatch }, { defaultParameters }) {
      commit(_types.mutations.SET_DEFAULT_PARAMETERS, defaultParameters)
      dispatch(_types.actions.INIT_FILTERS)
      dispatch(_types.actions.INIT_DIMENSION)
    },
    [_types.actions.INIT_FILTERS] ({ dispatch, rootState }) {
      const storeFilters = { ...rootState.salesAnalysis.filters.selectedStoreFilters }
      const productFilters = { ...rootState.salesAnalysis.filters.selectedProductFilters }
      dispatch(NAMESPACE + TypesFilters.actions.SET_SELECTED_FILTERS, { storeFilters, productFilters }, { root: true })
    },
    [_types.actions.INIT_DIMENSION] ({ dispatch, getters }) {
      const dimension = getters[_types.getters.DEFAULT_DIMENSION]
      dispatch(_types.actions.CHANGE_BASE_DIMENSION, dimension.id)
    },
    async [_types.actions.FETCH_LONG_GRID] ({ state, commit, rootState }, { dimension, filter }) {
      commit(_types.mutations.SET_GRID, null)
      const { metrics, filters, calculatedField } = state
      const factTableName = rootState.salesAnalysis.accumulatedSalesDateOption.fact_name
      const longGrid = await getLongGrid({ metrics, dimension, factTableName, strategyFilter: filter, filters, calculatedField })
      commit(_types.mutations.SET_GRID, longGrid)
    },
    [_types.actions.INIT_BREADCRUMB] ({ dispatch, commit, state, getters }, dimension) {
      commit(NAMESPACE_BREADCRUMB + TypesBreadCrumb.mutations.RESET, null, { root: true })
      const breadCrumbItemText = dimension.value
      dispatch(_types.actions.PUSH_BREADCRUMB_ITEM, { dimension, filter: undefined, breadCrumbItemText })
    },
    // TODO: Refactor, I don't like the if
    async [_types.actions.PUSH_BREADCRUMB_ITEM] ({ commit, dispatch }, { dimension, filter, breadCrumbItemText }) {
      await dispatch(_types.actions.FETCH_LONG_GRID, { dimension, filter })
      commit(NAMESPACE_BREADCRUMB + TypesBreadCrumb.mutations.PUSH_ITEM, { dimension, filter, text: breadCrumbItemText }, { root: true })
      if (filter) commit(NAMESPACE + TypesFilters.mutations.ADD_SELECTED_FILTER, filter, { root: true })
    },
    [_types.actions.CHANGE_PIVOT] ({ getters, dispatch, rootGetters }, { pivotedDimensionId, pivotedDimensionCellValue, filter }) {
      const dimension = rootGetters[TypesModule.getters.DIMENSION_BY_ID](pivotedDimensionId)
      const breadCrumbItemText = `${dimension.value} en ${pivotedDimensionCellValue}`
      dispatch(_types.actions.PUSH_BREADCRUMB_ITEM, { dimension, filter, breadCrumbItemText })
    },
    // TODO: Refactor, too much code in here!
    async [_types.actions.POP_UNTIL_BREADCRUMB] ({ state, commit, dispatch, rootGetters }, breadCrumbItem) {
      if (rootGetters[NAMESPACE_BREADCRUMB + TypesBreadCrumb.getters.LAST_ITEM] === breadCrumbItem) return
      while (rootGetters[NAMESPACE_BREADCRUMB + TypesBreadCrumb.getters.LAST_ITEM] !== breadCrumbItem) {
        const lastBreadCrumbItem = rootGetters[NAMESPACE_BREADCRUMB + TypesBreadCrumb.getters.LAST_ITEM]
        commit(NAMESPACE_BREADCRUMB + TypesBreadCrumb.mutations.REMOVE_ITEM, lastBreadCrumbItem, { root: true })
        dispatch(NAMESPACE + TypesFilters.actions.REMOVE_SELECTED_FILTER_IN_CASCADE, lastBreadCrumbItem.filter, { root: true })
      }
      await dispatch(_types.actions.FETCH_LONG_GRID, breadCrumbItem)
    },
    // TODO: Refactor, too much code in here!
    [_types.actions.CHANGE_BASE_DIMENSION] ({ dispatch, rootGetters, getters }, dimensionId) {
      const dimension = rootGetters[TypesModule.getters.DIMENSION_BY_ID](dimensionId)
      const pivotedFilters = getters[_types.getters.PIVOTED_FILTERS]
      pivotedFilters.forEach((pivotedFilter) => {
        dispatch(NAMESPACE + TypesFilters.actions.REMOVE_SELECTED_FILTER_IN_CASCADE, pivotedFilter, { root: true })
      })
      dispatch(_types.actions.INIT_BREADCRUMB, dimension)
    },
    [_types.actions.APPLY_FILTERS] ({ dispatch, getters, rootGetters }, { storeFilters, productFilters }) {
      dispatch(NAMESPACE + TypesFilters.actions.SET_SELECTED_FILTERS, { storeFilters, productFilters }, { root: true })
      const breadCrumbItem = rootGetters[NAMESPACE_BREADCRUMB + TypesBreadCrumb.getters.LAST_ITEM]
      dispatch(_types.actions.FETCH_LONG_GRID, breadCrumbItem)
    },
    async [_types.actions.DOWNLOAD_EXCEL] ({ state, rootState, rootGetters }) {
      const { metrics, filters, calculatedField } = state
      const factTableName = rootState.salesAnalysis.accumulatedSalesDateOption.fact_name
      const { dimension, filter: strategyFilter } = rootGetters[NAMESPACE_BREADCRUMB + TypesBreadCrumb.getters.LAST_ITEM]
      await downloadLongGridToExcel({ metrics, dimension, factTableName, strategyFilter, filters, calculatedField })
    },
    async [_types.actions.RESET] ({ commit }) {
      commit(NAMESPACE_BREADCRUMB + TypesBreadCrumb.mutations.RESET, null, { root: true })
      commit(_types.mutations.SET_GRID, null)
    },
    async [_types.actions.RESET] ({ commit }) {
      commit(NAMESPACE_BREADCRUMB + TypesBreadCrumb.mutations.RESET, null, { root: true })
      commit(_types.mutations.SET_GRID, null)
    },
    [_types.actions.SET_INIT_DATA_DATE_GRANULARITY] ({ commit, getters }, { filter, dimensionTextValue }) {
      const title = `Apertura de fechas ${dimensionTextValue}`
      const filterObject = filter ? getters[_types.getters.CREATE_FILTER_OBJECT](filter) : {}
      commit(TypesDateGranularity.mutations.SET_TITLE, title, { root: true })
      commit(TypesDateGranularity.mutations.SET_FILTER, filterObject, { root: true })
    }
  },
  mutations: {
    [_types.mutations.SET_DEFAULT_PARAMETERS] (state, defaultParameters) {
      const { metrics, calculated_field: calculatedField } = defaultParameters
      state.metrics = metrics
      state.calculatedField = calculatedField
    },
    [_types.mutations.SET_GRID] (state, longGrid) {
      state.grid = longGrid
    }
  },
  modules: {
    filters,
    [BREADCRUMB_MODULE_NAME]: breadCrumb
  }
}

export default LongGrid



// WEBPACK FOOTER //
// ./src/app/sales_analysis/store/LongGrid.js